import { useState, useMemo, memo, useCallback, useRef, useEffect } from 'react';
import { Button } from 'reactstrap';
import { ItemRow, ItemValueContainer, ErrorMessageText, COLUMN_CONFIG } from './ListStyles';
import GooglePlaceInput from '../../../../../../../components/GooglePlaceInput';
import { LocationTypeArray } from '../../../../../../../data';
import { COLORS } from '../ImportContentStyles';
import styled, { createGlobalStyle } from 'styled-components';

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const FormField = styled.div`
  border: 1px solid ${props => props.error ? COLORS.danger : '#d9d9d9'};
  border-radius: 5px;
  padding: ${props => props.noPadding ? '0' : '8px 10px'};
  background-color: ${props => props.error ? 'rgba(220, 53, 69, 0.05)' : 'white'};
  transition: all 0.2s;
  height: 38px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  
  &:focus-within {
    border-color: ${props => props.error ? COLORS.danger : COLORS.primary};
    box-shadow: 0 0 0 3px ${props => props.error ? 'rgba(220, 53, 69, 0.1)' : 'rgba(0, 123, 255, 0.1)'};
  }
`;

const StyledInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
  height: 22px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const ErrorIcon = styled.span`
  color: ${COLORS.danger};
  margin-right: 5px;
`;

const FieldErrorText = styled(ErrorMessageText)`
  margin-top: 3px;
  display: flex;
  align-items: center;
  font-size: 12px;
`;

const StyledSelect = styled.select`
  width: 100%;
  border: none;
  background: transparent;
  padding: 0 10px;
  font-size: 14px;
  height: 22px;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23444%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 10px;
  
  &:focus {
    outline: none;
  }
  
  &::-ms-expand {
    display: none;
  }
  
  /* These styles may help on some browsers */
  color: ${props => props.value === '' ? '#aaa' : 'inherit'};
`;

// Add a special class to help with JavaScript enhancement
const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SelectFormField = styled(FormField)`
  padding: 0;
`;

const StatusText = styled.div`
  font-size: 12px;
  color: ${props => props.error ? COLORS.danger : COLORS.secondary};
  margin-top: 4px;
`;

// Add this new styled component for consistent text padding
const TextContent = styled.div`
  padding: 0 10px;
  box-sizing: border-box;
`;

const ActionButton = styled(Button)`
  width: 100%;
  margin-top: ${props => props.hasError ? '4px' : '0'};
  background-color: ${props => props.saving ? COLORS.secondary : COLORS.primary};
  border-color: ${props => props.saving ? COLORS.secondary : COLORS.primary};
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.saving ? COLORS.secondary : COLORS.primaryDark};
    border-color: ${props => props.saving ? COLORS.secondary : COLORS.primaryDark};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const ActionContainer = styled(ItemValueContainer)`
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 15% !important;
`;

export const LocationRejectedItem = memo(({ item, updateItem, index, isAlternate, recordPendingEdit }) => {
  const [itemAddress, setItemAddress] = useState(item.address);
  const [itemLocationType, setItemLocationType] = useState(item.locationType);
  const [itemLocationName, setItemLocationName] = useState(item.locationName || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(item.error || '');
  
  const locationTypes = useMemo(() => LocationTypeArray, []);
  
  const disableSave = !itemAddress || !itemLocationType || !itemLocationName || isSubmitting;
  const randId = Math.random().toString(36).substring(2, 8);
  
  const handleSave = useCallback(async () => {
    if (disableSave) return;
    
    try {
      setIsSubmitting(true);
      setValidationError('');
      
      // Make sure we have all required fields
      if (!itemLocationName) {
        setValidationError('Location name is required');
        setIsSubmitting(false);
        return;
      }
      
      if (!itemAddress) {
        setValidationError('Address is required');
        setIsSubmitting(false);
        return;
      }
      
      if (!itemLocationType) {
        setValidationError('Location type is required');
        setIsSubmitting(false);
        return;
      }
      
      const updatedItem = {
        ...item,
        locationName: itemLocationName,
        address: itemAddress,
        locationType: itemLocationType,
        error: null,
        invalidAddress: false,
        missingAddress: false,
        missingLocationName: false,
        missingLocationType: false,
        invalidLocationType: false
      };
      
      if (typeof updateItem === 'function') {
        updateItem(updatedItem, index);
      } else {
        console.error('updateItem is not a function');
        setValidationError('Could not update item. Please try again.');
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving location:', error);
      setValidationError(error.message || 'Error saving data');
      setIsSubmitting(false);
    }
  }, [item, itemLocationName, itemAddress, itemLocationType, updateItem, index, disableSave]);

  const handleLocationTypeChange = (e) => {
    const newValue = e.target.value;
    setItemLocationType(newValue);
    setValidationError('');
    
    // Record this change for bulk save functionality
    if (recordPendingEdit) {
      recordPendingEdit(index, 'locationType', newValue);
    }
  };

  const handleAddressChange = (value) => {
    console.log('Address changed:', value);
    if (value && typeof value === 'object') {
      // Make sure we're getting a valid address object
      setItemAddress(value);
      setValidationError('');
      
      // Record this change for bulk save functionality
      if (recordPendingEdit) {
        recordPendingEdit(index, 'address', value);
      }
    } else {
      // If we get a string or other invalid value, log it but don't update
      console.warn('Received invalid address value:', value);
    }
  };

  const handleLocationNameChange = (e) => {
    const newValue = e.target.value;
    setItemLocationName(newValue);
    setValidationError('');
    
    // Record this change for bulk save functionality
    if (recordPendingEdit) {
      recordPendingEdit(index, 'locationName', newValue);
    }
  };

  // Determine error states for individual fields
  const hasLocationNameError = (!itemLocationName || item.missingLocationName);
  const hasAddressError = (!itemAddress || item.invalidAddress || item.missingAddress);
  const hasLocationTypeError = (!itemLocationType || item.invalidLocationType || item.missingLocationType);

  return (
    <ItemRow isAlternate={isAlternate}>
      <ItemValueContainer width={COLUMN_CONFIG.location[0].width} style={{ textAlign: 'left' }}>
        {item.locationName && !item.missingLocationName ? (
          <TextContent>{item.locationName}</TextContent>
        ) : (
          <InputWrapper>
            <FormField error={hasLocationNameError}>
              <StyledInput
                type="text"
                value={itemLocationName}
                onChange={handleLocationNameChange}
                placeholder="Enter location name"
                disabled={isSubmitting}
              />
            </FormField>
          </InputWrapper>
        )}
      </ItemValueContainer>

      <ItemValueContainer width={COLUMN_CONFIG.location[1].width} style={{ textAlign: 'left' }}>
        {item.address && !item.invalidAddress && !item.missingAddress ? (
          <TextContent>{item.address.formattedAddress}</TextContent>
        ) : (
          <InputWrapper>
            {/* Display the original address if the address was autocorrected */}
            {item.autocorrectedAddress && item.originalAddress && (
              <FieldErrorText>
                <ErrorIcon>⚠️</ErrorIcon>
                Original: {item.originalAddress}
              </FieldErrorText>
            )}
            <FormField error={hasAddressError} noPadding>
              <GooglePlaceInput
                value={item.displayAddress || ''}
                onChange={handleAddressChange}
                placeholder={item.invalidAddress ? "Address is invalid or not found" : "Enter address"}
                inputId={`address${randId}`}
                disabled={isSubmitting}
              />
            </FormField>
          </InputWrapper>
        )}
      </ItemValueContainer>

      <ItemValueContainer width={COLUMN_CONFIG.location[2].width} style={{ textAlign: 'left' }}>
        {item.locationType && !item.missingLocationType && !item.invalidLocationType ? (
          <TextContent>{item.locationType}</TextContent>
        ) : (
          <InputWrapper>
            <SelectFormField error={hasLocationTypeError}>
              <SelectContainer>
                <StyledSelect
                  value={itemLocationType || ''}
                  onChange={handleLocationTypeChange}
                  disabled={isSubmitting}
                >
                  <option value="" disabled style={{padding: '10px', color: '#aaa'}}>Select type</option>
                  {locationTypes.map((type, idx) => (
                    <option key={idx} value={type} style={{padding: '10px'}}>
                      {type}
                    </option>
                  ))}
                </StyledSelect>
              </SelectContainer>
            </SelectFormField>
          </InputWrapper>
        )}
      </ItemValueContainer>

      <ItemValueContainer width={COLUMN_CONFIG.location[3].width} style={{ textAlign: 'left' }}>
        <TextContent>{item.contact?.phone || '-'}</TextContent>
      </ItemValueContainer>

      {/* Client column (COLUMN_CONFIG.location[4]) is hidden in rejected items list */}

      <ActionContainer width="15%" style={{ textAlign: 'left' }}>
        {validationError && validationError !== item.error && (
          <StatusText error>{validationError}</StatusText>
        )}
        <ActionButton 
          onClick={handleSave} 
          disabled={disableSave}
          size="sm"
          saving={isSubmitting}
          hasError={validationError && validationError !== item.error}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </ActionButton>
      </ActionContainer>
    </ItemRow>
  );
});